import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import { StatementsInvoiceSection } from '../../components/statements-invoice.components';
import { YearFilter } from '../../components/common/YearFilter';
import { useStatements } from '../../hooks/useStatements';
import { getItem } from '../../helper/localstorage.helper';
import { Skeleton } from '@mui/material';
import './../../scss/components/statements.style.scss';
// Imported only for the .mi-hero class used by the new heading. Every other
// rule in this file is scoped under .manage-invoice-v2 and can't leak here.
import './../../scss/components/manage-invoice.style.scss';

/* Tiny sparkles icon for the hero badge. Inline so the page has no
   new icon-library dependency. */
const HeroSparkles = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
        <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
    </svg>
);

/* Render the title with the LAST word styled as a violet gradient brand
   accent — works for any locale. Single-word titles are fully styled. */
const renderHeroTitle = (title: string) => {
    const trimmed = (title || '').trim();
    if (!trimmed) return null;
    const idx = trimmed.lastIndexOf(' ');
    if (idx === -1) {
        return <span className="mi-hero__brand">{trimmed}</span>;
    }
    const prefix = trimmed.slice(0, idx);
    const last = trimmed.slice(idx + 1);
    return (
        <>
            {prefix}{' '}
            <span className="mi-hero__brand">{last}</span>
        </>
    );
};

const WalletIcon: React.FC = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 7v10a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 11h-4a2 2 0 0 0 0 4h4a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

const CoinsIcon: React.FC = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="9" cy="6" rx="6" ry="3" stroke="currentColor" strokeWidth="2" />
        <path d="M3 6v5c0 1.66 2.69 3 6 3s6-1.34 6-3V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 11v5c0 1.66 2.69 3 6 3 1.2 0 2.32-.18 3.26-.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="17" cy="15" r="4.5" stroke="currentColor" strokeWidth="2" />
    </svg>
);

const ReceiptIcon: React.FC = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 3.5h14v17l-2.5-1.5L14 20.5 12 19l-2 1.5-2.5-1.5L5 20.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M9 8h6M9 12h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const CalendarBadgeIcon: React.FC = () => (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4.5" width="18" height="17" rx="2.5" stroke="currentColor" strokeWidth="2" />
        <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

function StatementsPage() {
    const { t } = useTranslation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const {
        statements,
        loading,
        error,
        handleYearChange,
        selectedYear,
    } = useStatements();

    // Guaranteed shimmer skeleton on first mount so chrome has time to settle.
    const [showInitialSkeleton, setShowInitialSkeleton] = useState<boolean>(true);
    useEffect(() => {
        const timer = setTimeout(() => setShowInitialSkeleton(false), 800);
        return () => clearTimeout(timer);
    }, []);

    const language = getItem('i18nextLng') || 'en-US';
    const currency = t('kr');
    const fmt = (n?: number) =>
        new Intl.NumberFormat(language.toLowerCase().includes('sv') ? 'sv-SE' : 'en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(Number(n) || 0);

    const grossSalary = Number(statements?.grossSalary) || 0;
    const netSalary = Number(statements?.salary) || 0;
    const totalDeductions =
        (Number(statements?.totalTax) || 0) +
        (Number(statements?.socialFee) || 0) +
        (Number(statements?.fakturaFee) || 0);
    const takeHomeRate = grossSalary > 0 ? Math.min(Math.max((netSalary / grossSalary) * 100, 0), 100) : 0;

    const showKpiSkeleton = (loading || showInitialSkeleton) && statements === null;

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId='statements-invoice'
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                {isSidebarOpen && (
                    <div
                        className="sidebar-overlay"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}
                <div className="dashboard-main-content">
                    <Header setIsSidebarOpen={setIsSidebarOpen} />

                    <div className="statements-v2">
                        {/* Hero header — same soft violet card used by My Customers,
                            Drafts, My Freelancers and Deductions. .mi-hero lives at
                            top level in manage-invoice.style.scss so this page just
                            consumes it without needing a .manage-invoice-v2 wrapper. */}
                        <div className="mi-hero">
                            <span className="mi-hero__badge">
                                <HeroSparkles />
                                {t('STATEMENTS_CENTER', { defaultValue: 'Statements Center' })}
                            </span>
                            <h1 className="mi-hero__title">
                                {renderHeroTitle(t('STATEMENTS_LABEL'))}
                            </h1>
                            <p className="mi-hero__subtitle">
                                {t('STATEMENTS_DESCRIPTION')}
                            </p>
                        </div>

                        {showKpiSkeleton ? (
                            // Two skeleton cards to match the two real KPIs the
                            // page renders (Gross Salary + Net Salary). The
                            // previous `[0, 1, 2]` left an orphan shimmer
                            // wrapping onto a second row of the 2-column grid.
                            <div className="statements-v2__kpis">
                                {[0, 1].map((i) => (
                                    <div className="kpi-card" key={i}>
                                        <div className="kpi-card__top">
                                            <Skeleton variant="text" width={90} height={14} animation="wave" />
                                            <Skeleton variant="circular" width={44} height={44} animation="wave" />
                                        </div>
                                        <Skeleton variant="text" width="70%" height={34} animation="wave" sx={{ mt: 1 }} />
                                        {i === 0 ? (
                                            <Skeleton variant="rounded" width="100%" height={8} animation="wave" sx={{ mt: 2, borderRadius: 999 }} />
                                        ) : (
                                            <Skeleton variant="text" width="45%" height={14} animation="wave" sx={{ mt: 1 }} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="statements-v2__kpis">
                                <div className="kpi-card">
                                    <div className="kpi-card__top">
                                        <p className="kpi-card__label">{t('GROSS_SALARY_LABEL', { defaultValue: 'Gross Salary' })}</p>
                                        <span className="kpi-card__icon kpi-card__icon--gross"><WalletIcon /></span>
                                    </div>
                                    <p className="kpi-card__amount">{fmt(grossSalary)} {currency}</p>
                                    <div className="kpi-card__progress-row">
                                        <span>{t('TAKE_HOME_RATE', { defaultValue: 'Take-home rate' })}</span>
                                        <span>{takeHomeRate.toFixed(1)}%</span>
                                    </div>
                                    <div className="kpi-card__progress-track">
                                        <div className="kpi-card__progress-fill" style={{ width: `${takeHomeRate}%` }} />
                                    </div>
                                </div>

                                <div className="kpi-card">
                                    <div className="kpi-card__top">
                                        <p className="kpi-card__label">{t('NET_SALARY', { defaultValue: 'Net Salary' })}</p>
                                        <span className="kpi-card__icon kpi-card__icon--net"><CoinsIcon /></span>
                                    </div>
                                    <p className="kpi-card__amount kpi-card__amount--net">{fmt(netSalary)} {currency}</p>
                                    <p className="kpi-card__hint">{t('PAID_OUT_TO_YOU', { defaultValue: 'Paid out to you' })}</p>
                                </div>

                              
                            </div>
                        )}

                        <div className="statements-v2__panel">
                            <div className="statements-v2__panel-head">
                                <div className="statements-v2__panel-titles">
                                    <h3 className="statements-v2__panel-title">
                                        {t('INCOME_BREAKDOWN', { defaultValue: 'Income Breakdown' })}
                                        <span className="statements-v2__year-badge">{selectedYear}</span>
                                    </h3>
                                    <p className="statements-v2__panel-hint">
                                        <CalendarBadgeIcon /> {t('INCOME_BREAKDOWN_HINT', { defaultValue: 'How your earnings break down for the selected year.' })}
                                    </p>
                                </div>
                                <YearFilter
                                    onYearChange={handleYearChange}
                                    initialYear={selectedYear}
                                    label={t('YEAR_LABEL')}
                                    className="statements-year-filter"
                                    minYear={2022}
                                    loading={loading}
                                />
                            </div>

                            <StatementsInvoiceSection
                                statements={statements}
                                loading={loading}
                                error={error}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StatementsPage;
